import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CircularProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import moment from 'moment';
import { getPatientPHIById } from '../../apis/patientPHISlice';
import PatientPHIStyle from './PatientPHI.module.scss';

const formatDate = (value) => (value ? moment(value).format('DD-MM-YYYY') : '—');
const yesNo = (value) => (value ? 'Yes' : 'No');
const text = (value) => (value === 0 ? '0' : value ? String(value) : '—');

function Field({ label, value }) {
  return (
    <div className={PatientPHIStyle.detailItem}>
      <label>{label}</label>
      <span>{value}</span>
    </div>
  );
}

function Section({ title, defaultExpanded = true, children }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        mb: 1,
        borderRadius: '8px !important',
        border: '1px solid var(--color-border-soft)',
        boxShadow: 'none',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography className={PatientPHIStyle.sectionTitle}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function PatientPHIDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { record, loading } = useSelector((state) => state.patientPHIData);

  useEffect(() => {
    if (id) dispatch(getPatientPHIById(id));
  }, [dispatch, id]);

  if (loading && !record?._id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!record?._id) {
    return (
      <div className={PatientPHIStyle.page}>
        <Typography className={PatientPHIStyle.pageTitle}>Record not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/patient-phi')} sx={{ mt: 2 }}>
          Back to list
        </Button>
      </div>
    );
  }

  const eh = record.emergencyContact || {};
  const bh = record.basicHealth || {};
  const cm = record.currentMedicalConditions || {};
  const sh = record.surgeryHistory || {};
  const meds = record.currentMedications || {};
  const vh = record.vaccinationHistory || {};
  const fh = record.familyMedicalHistory || {};
  const lh = record.lifestyleHabits || {};
  const ph = record.preventiveHealthHistory || {};
  const gh = record.geneticHealthInfo || {};
  const wh = record.womenHealth || {};
  const dn = record.doctorNotes || {};
  const ins = record.insuranceDetails || {};
  const cs = record.consent || {};

  return (
    <div className={PatientPHIStyle.page}>
      <Box className={`${PatientPHIStyle.pageHead} ${PatientPHIStyle.noPrint}`}>
        <Box>
          <Typography className={PatientPHIStyle.pageTitle}>Patient PHI Detail</Typography>
          <Typography className={PatientPHIStyle.pageSubTitle}>
            {record.patientId} · {record.fullName}
          </Typography>
        </Box>
        <div className={PatientPHIStyle.actions}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/patient-phi')}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            className={PatientPHIStyle.addBtn}
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/patient-phi/${record._id}/edit`)}
          >
            Edit
          </Button>
        </div>
      </Box>

      <Card className={PatientPHIStyle.formCard}>
        <Section title="1. Personal Details">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Patient ID" value={text(record.patientId)} />
            <Field label="Full Name" value={text(record.fullName)} />
            <Field label="Date of Birth" value={formatDate(record.dateOfBirth)} />
            <Field label="Age" value={text(record.age)} />
            <Field label="Gender" value={text(record.gender)} />
            <Field label="Mobile Number" value={text(record.mobileNumber)} />
            <Field label="WhatsApp Number" value={text(record.whatsappNumber)} />
            <Field label="Email" value={text(record.email)} />
            <Field label="Blood Group" value={text(record.bloodGroup)} />
            <Field label="DNA Test Report" value={text(record.dnaTestReport)} />
            <Field label="Marital Status" value={text(record.maritalStatus)} />
            <Field label="Occupation" value={text(record.occupation)} />
            <Field label="Address" value={text(record.address)} />
            <Field label="City" value={text(record.city)} />
            <Field label="State" value={text(record.state)} />
            <Field label="PIN Code" value={text(record.pinCode)} />
          </div>
        </Section>

        <Section title="2. Emergency Contact">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Contact Person" value={text(eh.contactPersonName)} />
            <Field label="Relationship" value={text(eh.relationship)} />
            <Field label="Mobile Number" value={text(eh.mobileNumber)} />
            <Field label="Alternate Contact" value={text(eh.alternateContactNumber)} />
          </div>
        </Section>

        <Section title="3. Basic Health Information">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Height (cm)" value={text(bh.heightCm)} />
            <Field label="Weight (kg)" value={text(bh.weightKg)} />
            <Field label="BMI" value={text(bh.bmi)} />
            <Field label="Blood Pressure" value={text(bh.bloodPressure)} />
            <Field label="Pulse Rate" value={text(bh.pulseRate)} />
            <Field label="Temperature" value={text(bh.temperature)} />
            <Field label="SpO2" value={text(bh.spo2)} />
            <Field label="Known Allergies" value={text(bh.knownAllergies)} />
          </div>
        </Section>

        <Section title="4. Current Medical Conditions">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Diabetes" value={yesNo(cm.diabetes)} />
            <Field label="High Blood Pressure" value={yesNo(cm.highBloodPressure)} />
            <Field label="Heart Disease" value={yesNo(cm.heartDisease)} />
            <Field label="Thyroid Disorder" value={yesNo(cm.thyroidDisorder)} />
            <Field label="Asthma / Respiratory" value={yesNo(cm.asthmaRespiratory)} />
            <Field label="Kidney Disease" value={yesNo(cm.kidneyDisease)} />
            <Field label="Liver Disease" value={yesNo(cm.liverDisease)} />
            <Field label="Cancer" value={yesNo(cm.cancer)} />
            <Field label="Arthritis / Joint" value={yesNo(cm.arthritisJoint)} />
            <Field label="Stroke" value={yesNo(cm.stroke)} />
            <Field label="High Cholesterol" value={yesNo(cm.highCholesterol)} />
            <Field label="Other Condition" value={text(cm.otherCondition)} />
          </div>
        </Section>

        <Section title="5. Surgery & Hospitalization History">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Has Surgery" value={yesNo(sh.hasSurgery)} />
            <Field label="Previous Hospitalizations" value={text(sh.previousHospitalizations)} />
          </div>
          {(sh.surgeries || []).length > 0 && (
            <Box sx={{ mt: 1.5 }} className={PatientPHIStyle.rowList}>
              {sh.surgeries.map((item, idx) => (
                <div key={idx} className={PatientPHIStyle.rowCard}>
                  <div className={PatientPHIStyle.detailGrid}>
                    <Field label="Surgery Name" value={text(item.surgeryName)} />
                    <Field label="Year" value={text(item.year)} />
                    <Field label="Hospital" value={text(item.hospital)} />
                  </div>
                </div>
              ))}
            </Box>
          )}
        </Section>

        <Section title="6. Current Medications">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Taking Regular Medicines" value={yesNo(meds.takingRegularMedicines)} />
          </div>
          {(meds.medications || []).length > 0 && (
            <Box sx={{ mt: 1.5 }} className={PatientPHIStyle.rowList}>
              {meds.medications.map((item, idx) => (
                <div key={idx} className={PatientPHIStyle.rowCard}>
                  <div className={PatientPHIStyle.detailGrid}>
                    <Field label="Medicine Name" value={text(item.medicineName)} />
                    <Field label="Dosage" value={text(item.dosage)} />
                    <Field label="Reason" value={text(item.reason)} />
                  </div>
                </div>
              ))}
            </Box>
          )}
        </Section>

        <Section title="7. Vaccination History">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="COVID-19" value={yesNo(vh.covid19)} />
            <Field label="COVID-19 Dose Details" value={text(vh.covid19DoseDetails)} />
            <Field label="Tetanus" value={yesNo(vh.tetanus)} />
            <Field label="Hepatitis B" value={yesNo(vh.hepatitisB)} />
            <Field label="Influenza" value={yesNo(vh.influenza)} />
            <Field label="Other Vaccines" value={text(vh.otherVaccines)} />
          </div>
        </Section>

        <Section title="8. Family Medical History">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Diabetes" value={yesNo(fh.diabetes)} />
            <Field label="Heart Disease" value={yesNo(fh.heartDisease)} />
            <Field label="Cancer" value={yesNo(fh.cancer)} />
            <Field label="Hypertension" value={yesNo(fh.hypertension)} />
            <Field label="Genetic Disorder" value={yesNo(fh.geneticDisorder)} />
            <Field label="Other Hereditary Illness" value={text(fh.otherHereditaryIllness)} />
          </div>
        </Section>

        <Section title="9. Lifestyle Habits">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Smoking" value={yesNo(lh.smoking)} />
            <Field label="Alcohol Consumption" value={yesNo(lh.alcoholConsumption)} />
            <Field label="Tobacco / Gutka" value={yesNo(lh.tobaccoGutka)} />
            <Field label="Exercise" value={text(lh.exercise)} />
            <Field label="Sleep Duration (hours)" value={text(lh.sleepDurationHours)} />
            <Field label="Stress Level" value={text(lh.stressLevel)} />
            <Field label="Diet Type" value={text(lh.dietType)} />
          </div>
        </Section>

        <Section title="10. Preventive Health History">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Last Health Checkup" value={formatDate(ph.lastHealthCheckupDate)} />
            <Field label="Last Blood Test" value={formatDate(ph.lastBloodTestDate)} />
            <Field label="Last ECG" value={formatDate(ph.lastECGDate)} />
            <Field label="Last X-Ray / Scan" value={formatDate(ph.lastXRayScanDate)} />
            <Field label="Last Dental Checkup" value={formatDate(ph.lastDentalCheckupDate)} />
            <Field label="Last Eye Checkup" value={formatDate(ph.lastEyeCheckupDate)} />
          </div>
        </Section>

        <Section title="11. Genetic / Advanced Health Information">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Genetic Testing Done" value={yesNo(gh.geneticTestingDone)} />
            <Field label="Genetic Testing Details" value={text(gh.geneticTestingDetails)} />
            <Field label="Family Genetic Disorders" value={text(gh.familyGeneticDisorders)} />
            <Field label="Organ Donor" value={yesNo(gh.organDonor)} />
            <Field label="Blood Transfusion History" value={yesNo(gh.bloodTransfusionHistory)} />
          </div>
        </Section>

        {record.gender === 'Female' && (
          <Section title="12. Women's Health">
            <div className={PatientPHIStyle.detailGrid}>
              <Field label="Menstrual Cycle Regular" value={yesNo(wh.menstrualCycleRegular)} />
              <Field label="Pregnancy History" value={text(wh.pregnancyHistory)} />
              <Field label="Gynecological Disease" value={text(wh.gynecologicalDisease)} />
              <Field label="Last Pap Smear Test" value={formatDate(wh.lastPapSmearTest)} />
            </div>
          </Section>
        )}

        <Section title="13. Doctor / Health Advisor Notes">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Symptoms / Complaints" value={text(dn.symptomsComplaints)} />
            <Field label="Doctor Observation" value={text(dn.doctorObservation)} />
            <Field label="Recommended Tests" value={text(dn.recommendedTests)} />
            <Field label="Advice / Treatment Plan" value={text(dn.adviceTreatmentPlan)} />
            <Field label="Doctor Name" value={text(dn.doctorName)} />
          </div>
        </Section>

        <Section title="14. Insurance / Mediclaim Details">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Insurance Company" value={text(ins.insuranceCompany)} />
            <Field label="Policy Number" value={text(ins.policyNumber)} />
            <Field label="Policy Holder Name" value={text(ins.policyHolderName)} />
            <Field label="Coverage Amount" value={text(ins.coverageAmount)} />
            <Field label="TPA Name" value={text(ins.tpaName)} />
          </div>
        </Section>

        <Section title="15. Consent & Declaration">
          <div className={PatientPHIStyle.detailGrid}>
            <Field label="Patient Name" value={text(cs.patientName)} />
            <Field label="Consent Given" value={yesNo(cs.consentGiven)} />
            <Field label="Consent Date" value={formatDate(cs.consentDate)} />
          </div>
        </Section>
      </Card>
    </div>
  );
}
